import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { useAuth } from '@/lib/auth/use-auth'
import { useReference } from '@/lib/reference/use-reference'
import type { AllowRow, Country } from '@/lib/reference'
import {
  exceptionIndex,
  fleetColumns,
  resolveCountry,
  verdictCounts,
} from '@/lib/reference/fleet'
import { countryGroupIndex } from '@/lib/reference/groups'
import { ReferenceTable, type Column } from '@/components/reference/ReferenceTable'
import { CapabilitySummary } from '@/components/reference/CapabilitySummary'
import { ExceptionDialog } from '@/components/reference/ExceptionDialog'

/** No group selected: the whole table. */
const ALL = 'all'

/**
 * ISO 3166-1 country list.
 *
 * Small and slow-moving, but load-bearing: it is what turns an airport into
 * "domestic" or "international", which decides which minimum ground time and
 * which city-pair limit apply to a turnaround.
 *
 * The capability column is the Capability matrix read back — the baseline for
 * this country, with any ad-hoc exception applied on top and marked as one.
 * Editing it here makes an exception; editing the rule itself is done on the
 * Capability screen.
 *
 * The tabs are **groups** (Settings -> Groups). Regions are built in and come
 * from the continent column of the airport table, which is the only place the
 * drop carries one; NON-VN is what LIMIT COUNTRY's `ITL` was reaching for.
 */
export function CountriesPage() {
  const { can } = useAuth()
  const {
    countries,
    airports,
    aircraft,
    countryCapability,
    capabilityException,
    countryGroups,
    create,
    update,
    remove,
  } = useReference()
  const [exception, setException] = useState<Country | null>(null)
  const [view, setView] = useState(ALL)

  const editable = can('settings.edit')

  const airportsPerCountry = useMemo(() => {
    const counts = new Map<string, number>()
    for (const airport of airports) {
      counts.set(airport.countryCode, (counts.get(airport.countryCode) ?? 0) + 1)
    }
    return counts
  }, [airports])

  const fleet = useMemo(() => fleetColumns(aircraft), [aircraft])
  const exceptions = useMemo(
    () => exceptionIndex(capabilityException, 'country'),
    [capabilityException],
  )
  const groups = useMemo(
    () => countryGroupIndex(countryGroups, countries, airports),
    [countryGroups, countries, airports],
  )

  const baselineFor = useCallback(
    (code: string) => (column: string) =>
      resolveCountry(countryCapability, undefined, code, column, groups).state,
    [countryCapability, groups],
  )

  const capabilityOf = useCallback(
    (code: string) =>
      verdictCounts(fleet, (column) =>
        resolveCountry(countryCapability, exceptions.get(code), code, column, groups),
      ),
    [fleet, countryCapability, exceptions, groups],
  )

  const rows = useMemo(() => {
    const members = groups.get(view)
    return members ? countries.filter((row) => members.has(row.code)) : countries
  }, [countries, groups, view])

  async function saveException(code: string, allow: AllowRow, reason: string) {
    const existing = exceptions.get(code)
    if (Object.keys(allow).length === 0) {
      if (existing) await remove('capabilityException', existing.id)
      return
    }
    if (existing) await update('capabilityException', existing.id, { allow, reason })
    else await create('capabilityException', { scope: 'country', subject: code, allow, reason })
  }

  const columns = useMemo<Column<Country>[]>(
    () => [
      {
        key: 'code',
        header: 'Code',
        value: (row) => row.code,
        editor: 'text',
        isKey: true,
        cellClassName: 'w-24',
        render: (row) => <span className="t-number-sm text-ink">{row.code}</span>,
      },
      { key: 'name', header: 'Name', value: (row) => row.name, editor: 'text' },
      {
        key: 'airports',
        header: 'Airports',
        value: (row) => airportsPerCountry.get(row.code) ?? 0,
        numeric: true,
        filter: 'none',
        cellClassName: 'w-28 text-right',
      },
      {
        key: 'capability',
        header: 'Aircraft',
        value: (row) => capabilityOf(row.code).yes,
        numeric: true,
        filter: 'none',
        cellClassName: 'w-44',
        render: (row) => (
          <CapabilitySummary
            counts={capabilityOf(row.code)}
            onEdit={editable ? () => setException(row) : undefined}
          />
        ),
      },
    ],
    [airportsPerCountry, capabilityOf, editable],
  )

  return (
    <section>
      <h1 className="t-title-lg text-ink">Countries</h1>

      <div className="mt-6">
        <ReferenceTable
          columns={columns}
          rows={rows}
          entity="Country"
          canEdit={editable}
          toolbar={
            <div className="flex flex-wrap rounded-[100px] bg-surface-strong p-1">
              <GroupTab active={view === ALL} onClick={() => setView(ALL)}>
                All <span className="t-number-sm">{countries.length}</span>
              </GroupTab>
              {countryGroups.map((group) => (
                <GroupTab
                  key={group.code}
                  active={view === group.code}
                  onClick={() => setView(group.code)}
                  title={group.name}
                >
                  {group.code}{' '}
                  <span className="t-number-sm">{groups.get(group.code)?.size ?? 0}</span>
                </GroupTab>
              ))}
            </div>
          }
          onCreate={(values) =>
            create('country', { code: values.code.trim().toUpperCase(), name: values.name.trim() })
          }
          onUpdate={(id, values) =>
            update('country', id, { code: values.code.trim().toUpperCase(), name: values.name.trim() })
          }
          onDelete={(id) => remove('country', id)}
        />
      </div>

      {exception && (
        <ExceptionDialog
          subject={exception.code}
          subtitle={exception.name}
          columns={fleet}
          baseline={baselineFor(exception.code)}
          current={exceptions.get(exception.code)}
          canEdit={editable}
          onClose={() => setException(null)}
          onSave={(allow, reason) => saveException(exception.code, allow, reason)}
        />
      )}
    </section>
  )
}

function GroupTab({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean
  onClick: () => void
  title?: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={active}
      className={`t-caption-strong inline-flex items-center gap-1.5 rounded-[100px] px-3.5 py-2
                  transition-colors ${active ? 'bg-canvas text-ink' : 'text-muted hover:text-body'}`}
    >
      {children}
    </button>
  )
}
